import { useState } from 'react';

import { randomUUID } from 'expo-crypto';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useUtensils } from 'features/cart/hooks/use-utensils';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { StyleSheet } from 'react-native';
import { Pressable } from 'react-native';
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated';
import { Icon } from 'ucl/components/icon/Icon';
import { ListItemCheckboxGroup } from 'ucl/components/list-item-checkbox-group';

import SanityBlockRenderer from 'components/sanity-block-renderer';
import { toaster } from 'components/toaster/toaster';
import useFoodware from 'hooks/use-foodware';
import { t } from 'state/intl/translations';
import { getServiceModeFromLS } from 'state/service-mode';
import { getDesignToken, primitive } from 'styles/constants';
import { emptySanityImage } from 'utils/sanity';

import { logCartAddToCart } from '../../utils/analytics';
import { CartSectionHeader } from '../cart-section-header';

export const FoodwareSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { featureFoodware, loading: foodwareLoading } = useFoodware();
  const { utensils, loading: utensilsLoading } = useUtensils();
  const loading = foodwareLoading || utensilsLoading;

  const { cartEntries, addItemToMinimalCart, removeItemFromMinimalCart } = useMinimalCart();

  const selectedItems = cartEntries
    .filter(entry => entry.type === MinimalCartEntryType.extra)
    .map(entry => entry.id);

  const listItems = utensils?.map(utensil => ({
    value: utensil.id,
    label: utensil.name + (utensil.isAvailable ? '' : ` (${t('unavailable')})`),
    accessibilityLabel: utensil.name,
    disabled: !utensil.isAvailable,
  }));

  const handleChange = (nextValues: string[]) => {
    const selectedItemsSet = new Set(selectedItems);
    const newlySelectedValue = nextValues.find(value => !selectedItemsSet.has(value));
    const previousCartEntries: ICartEntry[] = cartEntries.filter(
      entry => entry.type !== MinimalCartEntryType.extra
    );

    cartEntries.forEach(entry => {
      if (entry.type === MinimalCartEntryType.extra) {
        removeItemFromMinimalCart(entry);
      }
    });
    nextValues.forEach(value => {
      const utensil = utensils?.find(utensil => utensil.id === value);
      const newUtensilEntry = {
        id: value,
        cartId: randomUUID(),
        quantity: 1,
        name: utensil?.name ?? '',
        image: emptySanityImage(),
        url: '',
        type: MinimalCartEntryType.extra,
        children: [],
      };

      const serviceMode = getServiceModeFromLS();
      if (serviceMode) {
        logCartAddToCart({
          cartEntry: newUtensilEntry,
          previousCartEntries,
          serviceMode,
        });
      }

      addItemToMinimalCart(newUtensilEntry);
      if (value === newlySelectedValue) {
        toaster.positive(t('addToCartSuccess', { itemName: newUtensilEntry.name }));
      }
      previousCartEntries.push(newUtensilEntry);
    });
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return null;
  }
  if (!featureFoodware?.items?.length) {
    return null;
  }

  return (
    <Animated.View style={styles.container} testID="foodware">
      <CartSectionHeader
        title={featureFoodware?.title?.locale ?? ''}
        testID="cart-section-heading-label"
      />
      <Pressable style={styles.headerContainer} onPress={handleToggle} testID="foodware-toggle">
        <SanityBlockRenderer maxWidth="90%" content={featureFoodware?.description?.localeRaw} />
        <Icon variant={isExpanded ? 'collapse' : 'select'} />
      </Pressable>
      {isExpanded && (
        <Animated.View entering={FadeInUp.duration(200)} exiting={FadeOutUp.duration(200)}>
          <ListItemCheckboxGroup
            value={selectedItems}
            onChange={handleChange}
            listItems={listItems ?? []}
          />
        </Animated.View>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
    overflow: 'hidden',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
  },
});
